import React, { useEffect, useState } from 'react';
import '../styles/Authors.css';

const Guidelines = () => {
  const [activeSection, setActiveSection] = useState(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const winScroll = document.documentElement.scrollTop;
      const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrolled = (winScroll / height) * 100;
      setScrollProgress(scrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const sections = [
    { id: 'standards', title: 'Editorial Standards', icon: 'fas fa-certificate' },
    { id: 'evaluation', title: 'Evaluation Criteria', icon: 'fas fa-clipboard-check' },
    { id: 'review', title: 'Review Guidelines', icon: 'fas fa-users' },
    { id: 'decision', title: 'Decision Process', icon: 'fas fa-gavel' },
    { id: 'language', title: 'Language & Style', icon: 'fas fa-pen' },
    { id: 'figures', title: 'Figures & Tables', icon: 'fas fa-chart-bar' },
    { id: 'statistics', title: 'Statistical Guidelines', icon: 'fas fa-calculator' },
    { id: 'revision', title: 'Revision Guidelines', icon: 'fas fa-edit' },
  ];
  return (
    <>
      {/* Progress Bar */}
      <div className="position-fixed top-0 start-0 w-100" style={{ zIndex: 1000 }}>
        <div
          className="bg-gradient"
          style={{
            height: '4px',
            width: `${scrollProgress}%`,
            background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
            transition: 'width 0.3s ease'
          }}
        />
      </div>

      {/* Hero Section */}
      <div className="hero-section">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mx-auto text-center">
              <h1 className="hero-title">
                <i className="fas fa-book-open me-3"></i>
                Editorial Guidelines
              </h1>
              <p className="hero-subtitle">
                Comprehensive standards and procedures for manuscript evaluation and publication
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Navigation */}
      <div className="container py-4">
        <div className="row mb-4">
          <div className="col-lg-10 mx-auto">
            <div className="content-card">
              <h3 className="mb-3">Quick Navigation</h3>
              <div className="row">
                {sections.map((section, index) => (
                  <div key={section.id} className="col-md-3 col-sm-6 mb-3">
                    <button
                      className={`btn btn-outline-primary w-100 ${activeSection === section.id ? 'active' : ''}`}
                      onClick={() => {
                        setActiveSection(section.id);
                        document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      style={{
                        borderRadius: '10px',
                        padding: '0.75rem',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <i className={`${section.icon} me-2`}></i>
                      <small>{section.title}</small>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="row mb-5">
          <div className="col-lg-10 mx-auto">

          <div id="standards" className="content-card" style={{ animationDelay: '0.1s' }}>
            <div className="content-card-header">
              <h2 className="content-card-title">
                <i className="fas fa-certificate me-3 text-primary"></i>
                Editorial Standards
              </h2>
            </div>
              <p className="card-text">
                These guidelines outline the editorial standards and procedures followed by the 
                Federal University Wukari Journal of Agriculture and Life Sciences (FUWJALS) 
                to ensure consistent, high-quality publication.
              </p>
            </div>
          </div>

          <div id="evaluation" className="content-card" style={{ animationDelay: '0.2s' }}>
            <div className="content-card-header">
              <h2 className="content-card-title">
                <i className="fas fa-clipboard-check me-3 text-success"></i>
                Manuscript Evaluation Criteria
              </h2>
            </div>
              <div className="row">
                <div className="col-md-6">
                  <div className="review-card">
                    <h4 className="review-card-title">
                      <i className="fas fa-flask me-2"></i>
                      Scientific Merit
                    </h4>
                    <ul className="review-card-content">
                      <li>Originality and novelty</li>
                      <li>Scientific rigor and methodology</li>
                      <li>Statistical appropriateness</li>
                      <li>Reproducibility of results</li>
                      <li>Significance of findings</li>
                    </ul>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="review-card">
                    <h4 className="review-card-title">
                      <i className="fas fa-cogs me-2"></i>
                      Technical Quality
                    </h4>
                    <ul className="review-card-content">
                      <li>Clarity of presentation</li>
                      <li>Logical organization</li>
                      <li>Language quality</li>
                      <li>Figure and table quality</li>
                      <li>Reference accuracy</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div id="review" className="content-card" style={{ animationDelay: '0.3s' }}>
            <div className="content-card-header">
              <h2 className="content-card-title">
                <i className="fas fa-users me-3 text-info"></i>
                Review Process Guidelines
              </h2>
            </div>
              <h4>Initial Editorial Screening</h4>
              <ul>
                <li>Scope alignment with journal focus</li>
                <li>Technical and scientific quality assessment</li>
                <li>Ethical compliance verification</li>
                <li>Manuscript completeness check</li>
                <li>Language quality evaluation</li>
              </ul>
              
              <h4>Peer Review Assignment</h4>
              <ul>
                <li>Minimum two independent reviewers per manuscript</li>
                <li>Reviewers selected based on expertise</li>
                <li>Conflict of interest screening</li>
                <li>Geographic and institutional diversity consideration</li>
                <li>Reviewer performance tracking</li>
              </ul>
            </div>
          </div>
          
          <div id="decision" className="content-card" style={{ animationDelay: '0.4s' }}>
            <div className="content-card-header">
              <h2 className="content-card-title">
                <i className="fas fa-gavel me-3 text-warning"></i>
                Decision Making Process
              </h2>
            </div>
            <div className="card-body">
              <div className="alert alert-info" style={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)' }}>
                <h5>Decision Categories</h5>
                <ul className="mb-0">
                  <li><strong>Accept:</strong> No further changes required</li>
                  <li><strong>Minor Revision:</strong> Small changes needed</li>
                  <li><strong>Major Revision:</strong> Significant improvements required</li>
                  <li><strong>Reject and Resubmit:</strong> Fundamental issues, may resubmit</li>
                  <li><strong>Reject:</strong> Not suitable for publication</li>
                </ul>
              </div>
              
              <h4>Decision Criteria</h4>
              <ul>
                <li>Reviewer recommendations</li>
                <li>Editorial assessment</li>
                <li>Journal standards compliance</li>
                <li>Contribution to field knowledge</li>
                <li>Reader interest and impact potential</li>
              </ul>
            </div>
          </div>
          
          <div id="language" className="content-card" style={{ animationDelay: '0.5s' }}>
            <div className="content-card-header">
              <h2 className="content-card-title">
                <i className="fas fa-pen me-3 text-primary"></i>
                Language and Style Guidelines
              </h2>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <h4>Language Requirements</h4>
                  <ul>
                    <li>Clear, concise English</li>
                    <li>Scientific terminology accuracy</li>
                    <li>Consistent style throughout</li>
                    <li>Professional tone</li>
                    <li>Grammar and spelling accuracy</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h4>Writing Style</h4>
                  <ul>
                    <li>Third person, passive voice preferred</li>
                    <li>Past tense for methodology and results</li>
                    <li>Present tense for established facts</li>
                    <li>Avoid colloquialisms and jargon</li>
                    <li>Use standard abbreviations</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <div id="figures" className="content-card" style={{ animationDelay: '0.6s' }}>
            <div className="content-card-header">
              <h2 className="content-card-title">
                <i className="fas fa-chart-bar me-3 text-success"></i>
                Figure and Table Standards
              </h2>
            </div>
            <div className="card-body">
              <h4>Figure Requirements</h4>
              <ul>
                <li>High resolution (minimum 300 dpi)</li>
                <li>Clear, readable text and labels</li>
                <li>Appropriate size and scale</li>
                <li>Consistent style across figures</li>
                <li>Self-explanatory captions</li>
              </ul>
              
              <h4>Table Guidelines</h4>
              <ul>
                <li>Clear column and row headers</li>
                <li>Appropriate statistical presentation</li>
                <li>Consistent formatting</li>
                <li>Footnotes for explanations</li>
                <li>Logical data organization</li>
              </ul>
            </div>
          </div>
          
          <div id="statistics" className="content-card" style={{ animationDelay: '0.7s' }}>
            <div className="content-card-header">
              <h2 className="content-card-title">
                <i className="fas fa-calculator me-3 text-info"></i>
                Statistical Guidelines
              </h2>
            </div>
            <div className="card-body">
              <ul>
                <li>Appropriate statistical tests for data type</li>
                <li>Sample size justification</li>
                <li>Effect size reporting where applicable</li>
                <li>Confidence intervals inclusion</li>
                <li>Multiple comparisons correction</li>
                <li>Missing data handling description</li>
                <li>Software and version specification</li>
              </ul>
            </div>
          </div>
          
          <div id="revision" className="content-card" style={{ animationDelay: '0.8s' }}>
            <div className="content-card-header">
              <h2 className="content-card-title">
                <i className="fas fa-edit me-3 text-warning"></i>
                Revision Guidelines
              </h2>
            </div>
            <div className="card-body">
              <h4>For Authors</h4>
              <ul>
                <li>Address all reviewer comments</li>
                <li>Provide detailed response letter</li>
                <li>Mark changes in revised manuscript</li>
                <li>Submit within deadline (usually 4-6 weeks)</li>
                <li>Maintain original manuscript structure</li>
              </ul>
              
              <h4>For Editors</h4>
              <ul>
                <li>Evaluate response to reviewer comments</li>
                <li>Assess improvement in manuscript quality</li>
                <li>Consider need for re-review</li>
                <li>Make timely decisions</li>
                <li>Provide clear feedback</li>
              </ul>
            </div>
          </div>
          
          <div className="content-card" style={{ animationDelay: '0.9s' }}>
            <div className="content-card-header">
              <h2 className="content-card-title">
                <i className="fas fa-star me-3 text-danger"></i>
                Special Considerations
              </h2>
            </div>
            <div className="card-body">
              <h4>International Authors</h4>
              <ul>
                <li>Language editing services recommended</li>
                <li>Cultural sensitivity in review process</li>
                <li>Extended deadline considerations</li>
                <li>Fee waiver programs available</li>
              </ul>
              
              <h4>Student Authors</h4>
              <ul>
                <li>Supervisor involvement required</li>
                <li>Educational guidance provided</li>
                <li>Mentoring throughout process</li>
                <li>Special recognition opportunities</li>
              </ul>
            </div>
          </div>
          
          <div className="content-card" style={{ animationDelay: '1.0s' }}>
            <div className="content-card-header">
              <h2 className="content-card-title">
                <i className="fas fa-shield-alt me-3 text-success"></i>
                Quality Assurance
              </h2>
            </div>
            <div className="card-body">
              <ul>
                <li>Regular review of editorial processes</li>
                <li>Reviewer performance monitoring</li>
                <li>Author satisfaction surveys</li>
                <li>Continuous improvement initiatives</li>
                <li>International standard compliance</li>
                <li>Technology updates and training</li>
              </ul>
            </div>
          </div>
          
          <div className="content-card" style={{
            animationDelay: '1.1s',
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)'
          }}>
            <div className="content-card-header">
              <h2 className="content-card-title">
                <i className="fas fa-envelope me-3 text-primary"></i>
                Contact Information
              </h2>
            </div>
            <div className="card-body">
              <p className="card-text">
                For questions about editorial guidelines or submission procedures:
              </p>
              <p className="card-text">
                <strong>Editorial Office</strong><br />
                Email: editorial@fuwukari.edu.ng<br />
                Phone: +234 XXX XXX XXXX
              </p>
            </div>
          </div>
    </>
  );
};

export default Guidelines;